import { FC } from 'react'
import classNames from 'classnames'
import { IconChevronLeft, IconChevronRight } from 'icons'
import Button from 'components/Button'
import styles from './Pagination.module.scss'

export type TProps = {
  pages: {
    total: number
    current: number
    length?: 3 | 5
  }
  size?: 'small' | 'medium' | 'big'
  isLoading?: boolean
  showJumpPrevActions?: boolean
  showJumpPrevActionsOnLastPagination?: boolean
  showJumpNextActions?: boolean
  showJumpNextActionsOnFirstPagination?: boolean
  onClick: (page: number) => void
  onPrevClick: (page: number) => void
  onNextClick: (page: number) => void
}

const Pagination: FC<TProps> = ({
  pages,
  size = 'medium',
  isLoading,
  showJumpPrevActions,
  showJumpPrevActionsOnLastPagination,
  showJumpNextActions,
  showJumpNextActionsOnFirstPagination,
  onClick,
  onPrevClick,
  onNextClick,
}) => {
  const { total, current } = pages
  const pageSize = pages?.length || 5
  const pageOffset = pages?.length === 3 ? 1 : 2

  const emptyArray =
    total < pageSize ? [...Array<number>(total)] : [...Array<number>(pageSize)]

  const pagesArray = emptyArray.map((_, index) => {
    const initialPage = current - pageOffset + index

    if (total < pageSize) {
      return index + 1
    }

    if (
      initialPage >= 1 &&
      current >= pageOffset &&
      current >= pageSize - pageOffset &&
      current + pageOffset < total
    ) {
      return initialPage
    }
    if (current + pageOffset >= total) {
      return total - pageOffset * 2 + index
    }
    return index + 1
  })

  const isLastPage = pagesArray.includes(total)
  const isFirstPage = pagesArray.includes(1)
  const isJumpPrevActionsVisible =
    (showJumpPrevActionsOnLastPagination && isLastPage) ||
    (!showJumpPrevActionsOnLastPagination &&
      showJumpPrevActions &&
      current + pageOffset > pageSize)

  const isJumpNextActionsVisible =
    (showJumpNextActionsOnFirstPagination && isFirstPage) ||
    (!showJumpNextActionsOnFirstPagination &&
      showJumpNextActions &&
      pagesArray[pageSize - 1] < total)

  const showPagination = total > pageSize
  const prevButtonValue = current - 1
  const nextButtonValue = current + 1
  const prevDotsButtonValue = pagesArray[0] - 1
  const nextDotsButtonValue = pagesArray[pagesArray.length - 1] + 1

  const className = classNames(styles.ecPagination, {
    [styles.ecPaginationDisabled]: isLoading,
  })

  return (
    <nav
      aria-label="Paginação"
      className={className}
      data-testid="ecPagination"
    >
      <div className={styles.ecPaginationItem}>
        <Button
          shape="circle"
          theme="ghost"
          aria-label={`Voltar para página ${prevButtonValue}`}
          disabled={current === 1}
          size={size}
          Icon={{
            Component: IconChevronLeft,
            title: 'Ícone: Voltar',
            color: 'grey900',
          }}
          onClick={() => onPrevClick(prevButtonValue)}
        />
      </div>
      <ul>
        {showPagination && isJumpPrevActionsVisible && (
          <>
            <li className={styles.ecPaginationItem}>
              <Button
                shape="circle"
                theme="white"
                aria-label="Ir para página 1"
                size={size}
                onClick={() => onClick(1)}
              >
                1
              </Button>
            </li>
            {current + pageOffset > pageSize + 1 && (
              <li className={styles.ecPaginationItem}>
                <Button
                  shape="circle"
                  theme="white"
                  aria-label={`Voltar para página ${prevDotsButtonValue}`}
                  size={size}
                  onClick={() => onClick(prevDotsButtonValue)}
                >
                  ...
                </Button>
              </li>
            )}
          </>
        )}
        {pagesArray?.map((item: number) => {
          const key = `paginationIndex_${String(item)}`
          return (
            <li key={key} className={styles.ecPaginationItem}>
              <Button
                shape="circle"
                size={size}
                theme={item === current ? 'pink' : 'white'}
                aria-label={`Ir para página ${item}`}
                onClick={() => onClick(item)}
              >
                {item}
              </Button>
            </li>
          )
        })}
        {showPagination && isJumpNextActionsVisible && (
          <>
            <li className={styles.ecPaginationItem}>
              <Button
                theme="white"
                shape="circle"
                aria-label={`Avançar para página ${nextDotsButtonValue}`}
                size={size}
                onClick={() => onClick(nextDotsButtonValue)}
              >
                ...
              </Button>
            </li>
            <li className={styles.ecPaginationItem}>
              <Button
                theme="white"
                shape="circle"
                size={size}
                aria-label={`Ir para página ${total}`}
                onClick={() => onClick(total)}
              >
                {total}
              </Button>
            </li>
          </>
        )}
      </ul>
      <div className={styles.ecPaginationItem}>
        <Button
          shape="circle"
          theme="ghost"
          aria-label={`Avançar para página ${nextButtonValue}`}
          size={size}
          disabled={current === total}
          Icon={{
            Component: IconChevronRight,
            title: 'Ícone: Avançar',
            color: 'grey900',
          }}
          onClick={() => onNextClick(nextButtonValue)}
        />
      </div>
    </nav>
  )
}

export default Pagination
